/**
 * Turning a callable failure into something a business owner can act on.
 *
 * Firebase surfaces every non-HttpsError condition as the bare string
 * `"internal"` — including the one that is easiest to hit during a rollout: a
 * function the client knows about but that has not been deployed yet, which
 * comes back as a 404 and lands here as `functions/internal`. Showing that
 * verbatim tells the owner nothing, blames them for nothing they did, and gives
 * support no clue either.
 *
 * The messages we raise deliberately (`invalid-argument`, `failed-precondition`)
 * are already written for the owner, so they pass straight through. Everything
 * else is a fault on our side and is phrased as such.
 */

import type { TranslationKey } from '../../i18n/index.js';

/** Codes whose server-side message was written to be read by the owner. */
const PASS_THROUGH = new Set([
  'functions/invalid-argument',
  'functions/failed-precondition',
  'functions/already-exists',
  'functions/not-found',
  'functions/resource-exhausted',
]);

const BY_CODE: Record<string, TranslationKey> = {
  'functions/unauthenticated': 'error.unauthenticated',
  'functions/permission-denied': 'error.permissionDenied',
  'functions/unavailable': 'error.unavailable',
  'functions/deadline-exceeded': 'error.deadlineExceeded',
  'functions/internal': 'error.internal',
  'functions/unknown': 'error.internal',
};

/**
 * A message worth showing. `fallback` lets a caller add context the generic
 * text cannot know ("We couldn't save your business number.") — already
 * translated by the caller, since only it knows what it was doing.
 *
 * The translator is a parameter rather than a hook so this stays a plain
 * function callable from anywhere, including outside React (doc 20 D8).
 */
export function friendlyError(
  t: (key: TranslationKey) => string,
  error: unknown,
  fallback?: string,
): string {
  const code = (error as { code?: string } | null)?.code;
  const raw = (error as { message?: string } | null)?.message?.trim();

  if (code && PASS_THROUGH.has(code) && raw) return raw;
  if (code && BY_CODE[code]) {
    const message = t(BY_CODE[code]!);
    return fallback ? `${fallback} ${message}` : message;
  }

  // No code at all (a network throw, a bug in our own client code) — the raw
  // message is still better than nothing, unless it is one of the useless
  // one-word ones Firebase produces.
  if (raw && raw !== 'internal' && raw !== 'unknown') return raw;
  return fallback ? `${fallback}${t('error.retrySuffix')}` : t('error.internal');
}
